import { useQuery, type QueryClient } from '@tanstack/react-query';
import { api, unwrap } from '../../api/client';
import { catalogKeys } from './model';

const LOOKUP_STALE = 5 * 60_000;

export function useUnits() {
  return useQuery({
    queryKey: catalogKeys.units,
    queryFn: () => unwrap(api.GET('/units')).then((r) => r.data),
    staleTime: LOOKUP_STALE,
    // Setup may be open in another tab; pick up new categories/units when coming back.
    refetchOnWindowFocus: true,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: catalogKeys.categories,
    queryFn: () => unwrap(api.GET('/categories')).then((r) => r.data),
    staleTime: LOOKUP_STALE,
    // Setup may be open in another tab; pick up new categories/units when coming back.
    refetchOnWindowFocus: true,
  });
}

/** POS categories (the register tabs), in tab order. */
export function usePosCategories() {
  return useQuery({
    queryKey: catalogKeys.posCategories,
    queryFn: () => unwrap(api.GET('/pos/categories')).then((r) => r.data),
    staleTime: LOOKUP_STALE,
    refetchOnWindowFocus: true,
  });
}

export function useBrands() {
  return useQuery({
    queryKey: catalogKeys.brands,
    queryFn: () => unwrap(api.GET('/brands')).then((r) => r.data),
    staleTime: LOOKUP_STALE,
    // Setup may be open in another tab; pick up new categories/units when coming back.
    refetchOnWindowFocus: true,
  });
}

export function useAttributes() {
  return useQuery({
    queryKey: catalogKeys.attributes,
    queryFn: () => unwrap(api.GET('/attributes')).then((r) => r.data),
    staleTime: LOOKUP_STALE,
    // Setup may be open in another tab; pick up new categories/units when coming back.
    refetchOnWindowFocus: true,
  });
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: catalogKeys.product(id),
    queryFn: () => unwrap(api.GET('/products/{id}', { params: { path: { id } } })),
    enabled: !!id,
  });
}

/**
 * Product images need the bearer token, so they are fetched as blobs and shown through an object
 * URL. The URL lives as long as the cached query (images are small and few per product); removing
 * an image revokes it (see `forgetProductImage`).
 */
export function useProductImageUrl(productId: string, imageId: string | undefined) {
  const image = useQuery({
    queryKey: catalogKeys.image(imageId ?? ''),
    queryFn: async () => {
      const { data, response } = await api.GET('/products/{id}/images/{imageId}', {
        params: { path: { id: productId, imageId: imageId ?? '' } },
        parseAs: 'blob',
      });
      if (!response.ok || !(data instanceof Blob)) throw new Error('Image not available');
      return URL.createObjectURL(data);
    },
    enabled: !!imageId,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
  return {
    url: imageId ? (image.data ?? null) : null,
    loading: image.isLoading,
    failed: image.isError,
  };
}

export function forgetProductImage(qc: QueryClient, imageId: string): void {
  const url = qc.getQueryData<string>(catalogKeys.image(imageId));
  if (url) URL.revokeObjectURL(url);
  qc.removeQueries({ queryKey: catalogKeys.image(imageId) });
}
